import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/apiClient';
import { useAuthStore } from '../store/authStore';
import type { Reservation, NextGameAvailability } from '../types/reservation';

export function useNextGameAvailability() {
  return useQuery({
    queryKey: ['next-game-availability'],
    queryFn: () => api.get<NextGameAvailability>('/reservations/next-game'),
    refetchInterval: 30_000,
  });
}

export function useMyReservations() {
  const { user } = useAuthStore();
  return useQuery({
    queryKey: ['my-reservations'],
    queryFn: () => api.get<Reservation[]>('/reservations/me'),
    enabled: !!user,
  });
}

export function useMyNextGameReservation(gameDate: string | undefined) {
  const { data: reservations } = useMyReservations();
  if (!gameDate || !reservations) return null;
  return (
    reservations.find(
      (r) => r.gameDate === gameDate && ['PENDING', 'CONFIRMED', 'CANCELLED'].includes(r.status),
    ) ?? null
  );
}

export type CreateIntentResult =
  | { free: true; reservationId: string }
  | { free: false; clientSecret: string; reservationId: string };

export function useCreatePaymentIntent() {
  return useMutation({
    mutationFn: () => api.post<CreateIntentResult>('/payments/intent', {}),
  });
}

export function useCancelReservation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reservationId: string) => api.delete(`/reservations/${reservationId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['next-game-availability'] });
    },
  });
}
